
import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Loader2, ChevronsRight, FolderOpen, Files, ClipboardList } from 'lucide-react';
import { Sector, User, UserRole } from '@/types'; // Corrected import path

interface SectorManagementProps {
  sectors: Sector[];
  onSaveSector: (sector: Sector) => Promise<void>;
  onDeleteSector: (id: string, newParentIdForChildren?: string | null) => Promise<void>;
  onOpenCreateSectorModal: (parentId: string | null, sectorToEdit: Sector | null) => void; // Corrigido
  currentUser: User; // Novo prop
}

export const SectorManagement: React.FC<SectorManagementProps> = ({ 
  sectors, 
  onSaveSector, 
  onDeleteSector, 
  onOpenCreateSectorModal,
  currentUser // Destructure new prop
}) => {
  const isAdmin = currentUser.role === UserRole.ADMIN;

  const handleDeleteSectorClick = async (id: string) => {
    // Find if the sector has children
    const children = sectors.filter(s => s.parentId === id);

    if (children.length > 0) {
      const confirmMessage = `❗ ATENÇÃO: Este setor possui ${children.length} sub-setores. Se você excluir, eles se tornarão setores de nível superior (sem pai). Deseja continuar?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    } else {
      if (!window.confirm("❗ CUIDADO: Tem certeza que deseja excluir este setor?\n\nEsta ação pode deixar artigos existentes sem categoria definida.")) {
        return;
      }
    }

    try {
      await onDeleteSector(id); // Call the new onDeleteSector with children handling
    } catch (err: any) {
      console.error("Erro ao excluir setor:", err);
      alert(`Erro ao excluir setor: ${err.message}`);
    }
  };

  // Function to render sectors hierarchically
  const renderSectorTree = (currentSectors: Sector[], depth = 0) => {
    const sortedSectors = [...currentSectors].sort((a, b) => a.name.localeCompare(b.name));

    return sortedSectors.map(sector => {
      const children = sectors.filter(s => s.parentId === sector.id);
      
      return (
        <React.Fragment key={sector.id}>
          <div
            className="flex items-center justify-between py-4 px-6 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors"
            style={{ paddingLeft: `${24 + depth * 20}px` }} // Indent based on depth
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {depth > 0 && <ChevronsRight size={16} className="text-slate-400 flex-shrink-0" />}
              <span className="font-bold text-slate-700 truncate">{sector.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onOpenCreateSectorModal(null, sector)} // Abre o modal para editar o setor
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Editar Setor"
              >
                <Pencil size={18} />
              </button>
              <button
                onClick={() => handleDeleteSectorClick(sector.id)}
                className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                title="Excluir Setor"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
          {children.length > 0 && renderSectorTree(children, depth + 1)}
        </React.Fragment>
      );
    });
  };

  const topLevelSectors = sectors.filter(s => !s.parentId);

  return (
    <div className="bg-white rounded-[32px] shadow-sm border border-slate-200 overflow-hidden animate-fade-in">
      <div className="p-8 border-b flex justify-between items-center bg-slate-50/30">
        <div>
          <h3 className="font-black text-slate-800 uppercase">Gestão de Setores</h3>
          <p className="text-slate-500 text-sm">Organize as áreas da empresa em uma estrutura hierárquica.</p>
        </div>
        <button
          onClick={() => onOpenCreateSectorModal(null, null)} // Abre o modal para criar um novo setor top-level
          className="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase flex items-center gap-2 shadow-lg hover:bg-blue-700 transition-all"
        >
          <Plus size={16} /> Novo Setor
        </button>
      </div>
      
      {sectors.length === 0 ? (
          <div className="px-8 py-10 text-center text-slate-400 font-medium flex flex-col items-center justify-center">
            <ClipboardList size={48} className="mb-4 opacity-50" />
            Nenhum setor cadastrado. Crie o primeiro!
          </div>
      ) : (
          <div className="overflow-x-auto divide-y divide-slate-100">
            {renderSectorTree(topLevelSectors)}
          </div>
      )}
    </div>
  );
};
